import { Link } from 'react-router';
import { SITE_URL } from '../content/types';
import { PageMeta } from '../components/PageMeta';
import { FAQSection } from '../components/FAQSection';
import type { FAQItem } from '../components/FAQSection';
import { MarketingHeader } from '../components/marketing/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';

const aiFeatures = [
  {
    icon: '🪄',
    title: 'AI form builder',
    description:
      'Describe the survey you need — "post-onboarding NPS with a follow-up comment" — and Pollenate drafts the questions, types, and options for you to edit.',
  },
  {
    icon: '📊',
    title: 'AI insights',
    description:
      'Turn hundreds of raw comments into themes, sentiment breakdowns, and concrete recommendations with one click. Know what to fix next without reading everything.',
  },
  {
    icon: '🔍',
    title: 'Semantic search',
    description:
      'Search feedback by meaning. A query for "checkout is confusing" finds "paying was hard" and "couldn\'t figure out billing" — no keyword overlap required.',
  },
  {
    icon: '🧩',
    title: 'MCP server',
    description:
      'A built-in Model Context Protocol server lets Claude Desktop, Cursor, and any MCP client submit feedback, query stats, and browse inboxes directly.',
  },
  {
    icon: '🤖',
    title: 'Agent feedback ingestion',
    description:
      'AI agents and chatbots report user satisfaction as it happens — attach agent name, session ID, and arbitrary context to every event via one POST.',
  },
  {
    icon: '🎙️',
    title: 'Voice feedback',
    description:
      'Feedback pages support voice recordings alongside 17 other question types, so users can just say what they think — and you can analyze it later.',
  },
];

const aiFAQs: FAQItem[] = [
  {
    question: 'What does "AI-native" mean in Pollenate?',
    answer:
      'AI is built into each stage of the feedback loop: generating forms from a text prompt, semantic vector search over collected feedback, AI-generated insight reports with themes and sentiment, and an MCP server so AI agents can submit and query feedback programmatically.',
  },
  {
    question: 'How do AI agents submit feedback?',
    answer:
      'Two ways: a plain POST to /collect with an API key, or through the built-in MCP server at /mcp which exposes tools like submit_feedback and get_feedback_stats to Claude, Cursor, and other MCP clients.',
  },
  {
    question: 'How does semantic search work?',
    answer:
      'Feedback comments are converted to vector embeddings and stored in an isolated, per-organization index. Searches are embedded the same way and matched by cosine similarity, so results are ranked by meaning rather than exact keywords.',
  },
  {
    question: 'Which plans include the AI features?',
    answer:
      'Semantic search, the MCP server, agent ingestion via /collect, and the AI form builder are available to every organization — each plan includes a daily search allowance. AI insight reports are included on paid plans (Team and Enterprise).',
  },
  {
    question: 'Is my feedback data used to train AI models?',
    answer:
      'No. Your feedback is processed to serve your own features — search, insights, form generation — and is never used to train foundation models.',
  },
];

/**
 * AI-focused marketing page: positions Pollenate as an AI-native feedback
 * platform (form generation, insights, semantic search, MCP, agent ingestion).
 */
export function AiPage() {
  const url = `${SITE_URL}/ai`;

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'AI', item: url },
    ],
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aiFAQs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-[#fafafa] dark:bg-neutral-950">
      <PageMeta
        title="AI-Native Feedback Platform — Pollenate"
        description="Generate forms with AI, search feedback by meaning, get AI insight reports, and connect AI agents through a built-in MCP server. Feedback, AI-native."
        url={url}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd).replace(/</g, '\\u003c') }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd).replace(/</g, '\\u003c') }} />

      <MarketingHeader />

      <main id="main-content">
        {/* ---- Hero ---- */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
              AI-Native Feedback
            </p>
            <h1 className="mt-2 text-3xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
              Feedback that understands itself
            </h1>
            <p className="mt-4 text-lg text-slate-600 dark:text-neutral-400 max-w-2xl mx-auto">
              Build forms with a prompt. Search responses by meaning. Turn a thousand comments into three action
              items. And let your AI agents file feedback while they work.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link
                to="/login?mode=signup"
                className="inline-flex items-center rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:from-amber-600 hover:to-orange-600 transition-colors"
              >
                Try it free
              </Link>
              <Link
                to="/docs#ai-agents"
                className="inline-flex items-center rounded-lg border border-slate-300 dark:border-neutral-700 px-5 py-3 text-sm font-semibold text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-neutral-800 transition-colors"
              >
                AI agent docs
              </Link>
            </div>
          </div>
        </section>

        {/* ---- Feature grid ---- */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 sm:py-16 bg-white dark:bg-neutral-900/40 border-y border-slate-200 dark:border-neutral-800">
          <div className="max-w-6xl mx-auto">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {aiFeatures.map((f) => (
                <div
                  key={f.title}
                  className="rounded-2xl border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-6"
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/40 dark:to-orange-900/40 flex items-center justify-center text-xl">
                    {f.icon}
                  </div>
                  <h3 className="mt-4 font-semibold text-slate-900 dark:text-white">{f.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600 dark:text-neutral-400">{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---- How the loop works ---- */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-2xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
                AI at every step of the loop
              </h2>
            </div>
            <div className="mt-10 space-y-4">
              {[
                {
                  step: '01',
                  title: 'Create',
                  desc: 'Prompt the AI form builder to draft your survey — 18 question types including voice — then tweak and publish.',
                },
                {
                  step: '02',
                  title: 'Collect',
                  desc: 'Widgets, feedback pages, the REST API, Zapier, and MCP-connected agents all feed the same inboxes.',
                },
                {
                  step: '03',
                  title: 'Understand',
                  desc: 'Semantic search finds what users mean; AI insight reports surface themes, sentiment, and recommendations.',
                },
                {
                  step: '04',
                  title: 'Act',
                  desc: 'Webhook automations, issue tracker links, and status workflows turn insight into shipped fixes.',
                },
              ].map((s) => (
                <div
                  key={s.step}
                  className="flex items-start gap-5 rounded-2xl border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-6"
                >
                  <span className="text-2xl font-bold text-amber-500/60 font-mono">{s.step}</span>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">{s.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-neutral-400">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-8 text-center text-sm text-slate-500 dark:text-neutral-400">
              Building with the API instead?{' '}
              <Link to="/developers" className="text-amber-600 dark:text-amber-400 hover:underline">
                See the Developer Feedback API &rarr;
              </Link>
            </p>
          </div>
        </section>

        {/* ---- FAQ ---- */}
        <div className="bg-white dark:bg-neutral-900/40 border-y border-slate-200 dark:border-neutral-800">
          <FAQSection faqs={aiFAQs} id="ai" injectSchema={false} />
        </div>

        {/* ---- Final CTA ---- */}
        <section className="px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl mx-auto text-center rounded-3xl border border-amber-200/60 dark:border-amber-700/40 bg-gradient-to-b from-amber-50 to-orange-50/50 dark:from-amber-950/30 dark:to-orange-950/20 px-6 py-12">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
              Stop reading feedback. Start understanding it.
            </h2>
            <p className="mt-3 text-lg text-slate-600 dark:text-neutral-400">
              Free to start — 5,000 events a month, no credit card.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/login?mode=signup"
                className="inline-flex items-center rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:from-amber-600 hover:to-orange-600 transition-colors"
              >
                Get started free
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center rounded-lg border border-slate-300 dark:border-neutral-700 px-5 py-3 text-sm font-semibold text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-neutral-800 transition-colors"
              >
                View pricing
              </Link>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
